import { Box, Textarea, Flex, Text } from "@chakra-ui/react";
import Field from "components/Input/Field";
import { useForm } from "react-hook-form";
import { Row, Col } from "antd";
import UploadImagem from "components/Input/UploadImagem";
import PrimaryButton from "components/Button/PrimaryButton";
import InputMask from "components/Input/InputMask";
import { isValidCPF, isValidCNPJ } from "@brazilian-utils/brazilian-utils";
import { PessoaTipo } from "core/features/pessoas/typings";
import { DataParceiroForm } from "core/features/parceiros/typings";
import { useEffect } from "react";
import SelectCategoriaParceiroProps from "components/Input/SelectCategoriaParceiro/index";
import SelectType from "./components/SelectType";
import history from "infra/helpers/history";
import OutlineButton from "ui/components/Button/OutlineButton";

function DadosDoParceiro({
  idParceiro,
  value,
  onSubmit,
  isLoading,
}: {
  idParceiro: string;
  value?: any;
  isLoading: boolean;
  onSubmit: (v: any) => void;
}) {
  const { handleSubmit, reset, watch, control } = useForm<DataParceiroForm>({});

  useEffect(() => {
    reset(value);
  }, [value, reset]);

  const tipoPessoa = watch("pessoaTipo");
  return (
    <>
      <form>
        <div>
          <Box>
            <Row gutter={40} justify="space-between">
              <Col sm={12}>
                <Text mb="16px" fontSize="18px" fontWeight="bold">
                  {idParceiro ? "Editar" : "Cadastrar"} Parceiro
                </Text>

                <Field
                  control={control}
                  name="nome"
                  label="Nome"
                  rules={{ required: "Campo obrigatório" }}
                />
              </Col>
              <Col sm={5}>
                <Field
                  control={control}
                  name="imagemUrl"
                  render={({ field }) => {
                    return (
                      <UploadImagem {...field} confirmButton="Enviar foto" />
                    );
                  }}
                />
              </Col>
            </Row>
          </Box>

          <Box mb="16px">
            <Row gutter={24}>
              <Col sm={8}>
                <Box mb={4}>
                  <Field
                    control={control}
                    name="pessoaTipo"
                    label="Tipo"
                    render={({ field }) => {
                      return <SelectType {...field} />;
                    }}
                  />
                </Box>
              </Col>

              <Col sm={8}>
                {tipoPessoa === PessoaTipo.Fisica ? (
                  <Field
                    control={control}
                    name="documento"
                    label="CPF"
                    rules={{
                      required: "Campo obrigatório",
                      validate: (v) => isValidCPF(v) || "CPF inválido",
                    }}
                    render={({ field, isInvalid }) => {
                      return (
                        <InputMask
                          {...field}
                          isInvalid={isInvalid}
                          mask="999.999.999-99"
                          placeholder="XXX.XXX.XXX-XX"
                        />
                      );
                    }}
                  />
                ) : (
                  <Field
                    control={control}
                    name="documento"
                    label="CNPJ"
                    rules={{
                      required: "Campo obrigatório",
                      validate: (v) => isValidCNPJ(v) || "CNPJ inválido",
                    }}
                    render={({ field, isInvalid }) => {
                      return (
                        <InputMask
                          {...field}
                          isInvalid={isInvalid}
                          mask="99.999.999/9999-99"
                          placeholder="XX.XXX.XXX/XXXX-XX"
                        />
                      );
                    }}
                  />
                )}
              </Col>

              <Col sm={8}>
                <Field
                  label="Categoria"
                  control={control}
                  name="categoriaParceiroId"
                  rules={{ required: "Campo obrigatório" }}
                  render={({ field }) => {
                    return <SelectCategoriaParceiroProps {...field} />;
                  }}
                />
              </Col>

              <Col sm={8}>
                <Box mb={4}>
                  <Field
                    label="Telefone"
                    control={control}
                    name="telefone"
                    render={({ field }) => (
                      <InputMask
                        {...field}
                        placeholder="(00) 00000-0000"
                        mask="(99) 99999-9999"
                      />
                    )}
                  />
                </Box>
              </Col>
              {tipoPessoa === PessoaTipo.Fisica ? (
                <Col sm={8}>
                  <Field
                    label="Data de nascimento"
                    name="dataNascimento"
                    inputProps={{ type: "date" }}
                    control={control}
                  />
                </Col>
              ) : null}

              <Col sm={8}>
                <Field control={control} name="email" label="Email" />
              </Col>
              <Col sm={12}>
                <Box mb={4}>
                  <Field
                    label="Whatsapp"
                    control={control}
                    name="whatsapp"
                    render={({ field }) => (
                      <InputMask
                        {...field}
                        placeholder="(00) 00000-0000"
                        mask="(99) 99999-9999"
                      />
                    )}
                  />
                </Box>
              </Col>
              <Col sm={12}>
                <Field
                  label="Instagram"
                  name="instagram"
                  inputProps={{
                    placeholder: "https://instagram.com/seuperfil",
                  }}
                  control={control}
                />
              </Col>
              <Col sm={12}>
                <Box mb={4}>
                  <Field
                    label="Facebook"
                    name="facebook"
                    inputProps={{
                      placeholder: "https://facebook.com/seuperfil",
                    }}
                    control={control}
                  />
                </Box>
              </Col>
              <Col sm={12}>
                <Field
                  label="Site"
                  name="site"
                  inputProps={{
                    placeholder: "https://seusite.com.br",
                  }}
                  control={control}
                />
              </Col>
              <Col sm={24}>
                <Field
                  label="Sobre a empresa"
                  name="sobre"
                  inputProps={{ type: "phone" }}
                  rules={{
                    required: "Informe detalhes sobre a empresa",
                  }}
                  control={control}
                  render={({ field, isInvalid }) => (
                    <Textarea
                      isInvalid={isInvalid}
                      className="chakra-input"
                      rows={4}
                      placeholder="Digite..."
                      {...field}
                    />
                  )}
                />
              </Col>
            </Row>
          </Box>
        </div>
        <Flex justifyContent={"flex-end"} mt="32px" gap={4}>
          <OutlineButton
            onClick={() => history.push("/administracao/parceiros")}
          >
            Cancelar
          </OutlineButton>
          <PrimaryButton
            isDisabled={isLoading}
            isLoading={isLoading}
            onClick={() => {
              handleSubmit(onSubmit)();
            }}
          >
            Salvar
          </PrimaryButton>
        </Flex>
      </form>
    </>
  );
}

export default DadosDoParceiro;
